import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /** sessionStorage key prefix for collection “Load more” restored depth (pathname + search). */
  static COLLECTION_PAGES_KEY_PREFIX = 'hestra:coll-pages:';

  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /** @type {boolean} */
  #plpScrollSaveListenerAttached = false;

  connectedCallback() {
    super.connectedCallback();

    if (this.#normalizeCollectionPageUrl()) {
      return;
    }

    if (this.querySelector('[data-collection-load-more]') && !this.#isBackForwardCollectionNavigation()) {
      this.#clearStoredCollectionDepthForCurrentUrl();
    }

    if (this.querySelector('[data-collection-load-more]')) {
      window.addEventListener('pagehide', this.#savePlpScrollOnPagehide);
      this.#plpScrollSaveListenerAttached = true;
    }

    const eagerPlpRestore =
      this.#isBackForwardCollectionNavigation() &&
      Boolean(this.querySelector('[data-collection-load-more]')) &&
      this.#readStoredCollectionLastPage() >= 2;

    if (eagerPlpRestore) {
      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'manual';
      }
      this.#prepareEagerPlpRestoreVisual();
      queueMicrotask(() => {
        void this.#runEagerPlpRestoreAfterBack();
      });
    }

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    // Prefetch adjacent pages for infinite scroll, but not during initial load:
    // two synchronous section requests compete with LCP, images, and app.js.
    requestIdleCallback(
      () => {
        if (!this.isConnected) return;
        void (async () => {
          // Back/forward restore runs eagerly via queueMicrotask (see above), not here — avoids
          // painting a short SSR page + scroll before the cumulative grid is rebuilt.
          if (!this.#isBackForwardCollectionNavigation()) {
            await this.#fetchPage('next');
          }
          if (!this.isConnected) return;
          if (!this.#isBackForwardCollectionNavigation()) {
            await this.#fetchPage('next');
          }
          if (!this.isConnected) return;
          requestIdleCallback(() => {
            if (!this.isConnected) return;
            void this.#fetchPage('previous');
          });
        })();
      },
      { timeout: 2500 }
    );

    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#plpScrollSaveListenerAttached) {
      window.removeEventListener('pagehide', this.#savePlpScrollOnPagehide);
      this.#plpScrollSaveListenerAttached = false;
    }
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);

    this.#aspectRatioHelper?.processNewElements();

    this.#syncCollectionLoadMore();

    this.#persistCollectionLoadedDepth();
    this.#syncCollectionPageQueryParam();

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper?.processNewElements();

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * Product cards in the grid (read from the DOM — `refs.cards[]` updates async after append, which
   * broke Load more + session restore: persist saw max page 1 and never stored depth).
   */
  #gridProductCards() {
    const { grid } = this.refs;
    if (!(grid instanceof HTMLElement)) return null;
    return grid.querySelectorAll(':scope > [ref="cards[]"]');
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const items = this.#gridProductCards();
    if (!items?.length) return;

    const isPrevious = type === 'previous';
    const targetCard = items[isPrevious ? 0 : items.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Appends the next page of items (used by collection “Load more”, same pipeline as infinite scroll).
   *
   * @param {Event} [event]
   */
  loadNextPage = async (event) => {
    if (event && 'preventDefault' in event) {
      event.preventDefault();
    }

    const root = this.querySelector('[data-collection-load-more]');
    const btn = root?.querySelector('[data-load-more-button]');
    if (btn instanceof HTMLButtonElement) {
      if (btn.disabled || btn.hidden) return;
      btn.disabled = true;
    }

    try {
      await this.#renderNextPage();
    } finally {
      this.#syncCollectionLoadMore();
    }
  };

  /** Updates count copy and hides the load-more strip when everything is on screen. */
  #syncCollectionLoadMore() {
    const root = this.querySelector('[data-collection-load-more]');
    if (!(root instanceof HTMLElement)) return;

    const items = this.#gridProductCards();
    const total = Number(root.dataset.totalProducts) || 0;
    const visible = items?.length ?? 0;

    const countEl = root.querySelector('[data-load-more-count]');
    const tpl = countEl?.dataset?.countTemplate;
    if (countEl instanceof HTMLElement && tpl) {
      countEl.textContent = tpl
        .replace(/__START__/g, '1')
        .replace(/__END__/g, String(visible))
        .replace(/__TOTAL__/g, String(total));
    }

    const next = this.#getPage('next');
    const hasNext = Boolean(next && this.#shouldUsePage(next));
    const loadBtn = root.querySelector('[data-load-more-button]');
    if (loadBtn instanceof HTMLButtonElement) {
      // Mirror the SSR contract from collection-load-more.liquid: keep the button (and the wrapper)
      // visible at the last page and reflect end-of-list state via `disabled` instead of removing it.
      // This preserves the disabled "Show more" button + the page-number strip after a JS-triggered
      // last load, matching what a hard reload of the same URL renders.
      loadBtn.disabled = !hasNext;
      loadBtn.setAttribute('aria-disabled', String(!hasNext));
    }
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.#clearPageQueryParamFromUrl();
    this.pages.clear();
    this.#clearStoredCollectionPagesForPath();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };

  /**
   * Normalizes invalid `page` query values. Keeps valid `?page=N` for Shopify SSR (SEO / shareable URLs).
   */
  #normalizeCollectionPageUrl() {
    if (!this.querySelector('[data-collection-load-more]')) return false;

    const u = new URL(window.location.href);
    const raw = u.searchParams.get('page');
    if (raw == null || raw === '') return false;

    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 1) {
      u.searchParams.delete('page');
      history.replaceState({}, '', u.pathname + u.search + u.hash);
      return false;
    }

    // Valid page ≥ 2: leave URL and first paint as Shopify paginated SSR (do not strip + reload).
    return false;
  }

  /**
   * Writes `?page=` to match the deepest product page currently in the grid (load more / restore).
   * Uses replaceState so filters and other params stay intact. Removing `page` when only page 1 is shown.
   *
   * Note: After load more, a full refresh serves SSR for that page number only (Shopify paginate),
   * not the previous cumulative client-only stack.
   */
  #syncCollectionPageQueryParam() {
    if (!this.querySelector('[data-collection-load-more]')) return;

    const last = this.#maxCardPage();
    const u = new URL(window.location.href);
    if (last <= 1) {
      u.searchParams.delete('page');
    } else {
      u.searchParams.set('page', String(last));
    }

    const next = `${u.pathname}${u.search}${u.hash}`;
    const cur = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (next !== cur) {
      history.replaceState(null, '', next);
    }
  }

  /** Resets `page` when filters change (results re-render from page 1). */
  #clearPageQueryParamFromUrl() {
    if (!this.querySelector('[data-collection-load-more]')) return;
    const u = new URL(window.location.href);
    if (!u.searchParams.has('page')) return;
    u.searchParams.delete('page');
    history.replaceState(null, '', u.pathname + u.search + u.hash);
  }

  /**
   * Stable across `?page=` (omit page so session keys match for the same collection + facet filters).
   */
  #collectionPagesStorageKey() {
    const u = new URL(window.location.href);
    u.searchParams.delete('page');
    return `${PaginatedList.COLLECTION_PAGES_KEY_PREFIX}${encodeURIComponent(u.pathname + u.search)}`;
  }

  /** Only restore “Load more” depth after browser Back/Forward, not a fresh link visit or reload. */
  #isBackForwardCollectionNavigation() {
    const [nav] = performance.getEntriesByType('navigation');
    if (nav && 'type' in nav && typeof nav.type === 'string') {
      return nav.type === 'back_forward';
    }
    // Deprecated API (e.g. very old browsers): 2 === TYPE_BACK_FORWARD
    try {
      return performance.navigation?.type === 2;
    } catch {
      return false;
    }
  }

  #clearStoredCollectionDepthForCurrentUrl() {
    if (!this.querySelector('[data-collection-load-more]')) return;
    try {
      sessionStorage.removeItem(this.#collectionPagesStorageKey());
    } catch {
      /* ignore */
    }
  }

  #readStoredCollectionLastPage() {
    if (!this.querySelector('[data-collection-load-more]')) return 1;
    try {
      const raw = sessionStorage.getItem(this.#collectionPagesStorageKey());
      if (!raw) return 1;
      const parsed = JSON.parse(raw);
      const last = Number(parsed?.last);
      if (!Number.isFinite(last) || last < 2) return 1;
      return last;
    } catch {
      return 1;
    }
  }

  #persistCollectionLoadedDepth() {
    if (!this.querySelector('[data-collection-load-more]')) return;
    const last = this.#maxCardPage();
    const key = this.#collectionPagesStorageKey();
    /** @type {Record<string, unknown>} */
    let prev = {};
    try {
      const raw = sessionStorage.getItem(key);
      if (raw) prev = JSON.parse(raw);
    } catch {
      prev = {};
    }
    if (last < 2) {
      try {
        sessionStorage.removeItem(key);
      } catch {
        /* ignore */
      }
      return;
    }
    try {
      sessionStorage.setItem(key, JSON.stringify({ ...prev, last }));
    } catch {
      /* quota */
    }
  }

  #clearStoredCollectionPagesForPath() {
    if (!this.querySelector('[data-collection-load-more]')) return;
    const prefix = PaginatedList.COLLECTION_PAGES_KEY_PREFIX;
    const path = window.location.pathname;
    try {
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const k = sessionStorage.key(i);
        if (!k?.startsWith(prefix)) continue;
        const rest = k.slice(prefix.length);
        let decoded = rest;
        try {
          decoded = decodeURIComponent(rest);
        } catch {
          /* use raw */
        }
        if (decoded.startsWith(path)) {
          sessionStorage.removeItem(k);
        }
      }
    } catch {
      /* ignore */
    }
  }

  #maxCardPage() {
    const items = this.#gridProductCards();
    if (!items?.length) return 1;
    return Math.max(...[...items].map((c) => Number(c.dataset.page) || 1));
  }

  /**
   * Masks PLP during cumulative restore. Uses opacity (not visibility) so native lazy + listing
   * `data-deferred-viewport` observers can still intersect and load images.
   */
  #prepareEagerPlpRestoreVisual() {
    const { grid } = this.refs;
    if (grid instanceof HTMLElement) {
      grid.style.opacity = '0';
      grid.style.pointerEvents = 'none';
      grid.setAttribute('aria-busy', 'true');
    }
    const loadStrip = this.querySelector('[data-collection-load-more]');
    if (loadStrip instanceof HTMLElement) {
      loadStrip.style.opacity = '0';
      loadStrip.style.pointerEvents = 'none';
    }
    this.setAttribute('aria-busy', 'true');
  }

  #clearEagerPlpRestoreVisual() {
    const { grid } = this.refs;
    if (grid instanceof HTMLElement) {
      grid.style.opacity = '';
      grid.style.pointerEvents = '';
      grid.removeAttribute('aria-busy');
    }
    const loadStrip = this.querySelector('[data-collection-load-more]');
    if (loadStrip instanceof HTMLElement) {
      loadStrip.style.opacity = '';
      loadStrip.style.pointerEvents = '';
    }
    this.removeAttribute('aria-busy');
  }

  async #runEagerPlpRestoreAfterBack() {
    let savedScroll = null;
    try {
      await this.#restoreCollectionPagesFromSession();
      savedScroll = this.#takeStoredCollectionScrollY();
    } finally {
      if (this.isConnected) {
        this.#clearEagerPlpRestoreVisual();
      }
      if (this.isConnected && savedScroll != null) {
        this.#applyRestoredScroll(savedScroll);
      }
    }
  }

  #savePlpScrollOnPagehide = () => {
    if (!this.querySelector('[data-collection-load-more]')) return;
    if (this.#maxCardPage() < 2) return;
    const key = this.#collectionPagesStorageKey();
    try {
      let prev = {};
      const raw = sessionStorage.getItem(key);
      if (raw) prev = JSON.parse(raw);
      sessionStorage.setItem(key, JSON.stringify({ ...prev, scrollY: window.scrollY }));
    } catch {
      /* ignore */
    }
  };

  /** @returns {number | null} */
  #takeStoredCollectionScrollY() {
    try {
      const key = this.#collectionPagesStorageKey();
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const y = Number(parsed?.scrollY);
      if (!Number.isFinite(y)) return null;
      const { scrollY: _sy, ...rest } = parsed;
      sessionStorage.setItem(key, JSON.stringify(rest));
      return y;
    } catch {
      return null;
    }
  }

  /** @param {number} y */
  #clampScrollToSaved(y) {
    const maxY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    window.scrollTo({ top: Math.min(Math.max(0, y), maxY), behavior: 'instant' });
  }

  /** @param {number} y */
  #applyRestoredScroll(y) {
    this.#clampScrollToSaved(y);
    this.#promoteLazyImagesInViewportBand();
    this.#hydrateViewportDeferredImagesInBand();

    const reclamp = () => {
      this.#clampScrollToSaved(y);
      this.#promoteLazyImagesInViewportBand();
      this.#hydrateViewportDeferredImagesInBand();
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(reclamp);
    });

    window.setTimeout(reclamp, 120);
    window.setTimeout(() => {
      reclamp();
      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'auto';
      }
    }, 450);
  }

  #promoteLazyImagesInViewportBand() {
    const { grid } = this.refs;
    if (!(grid instanceof Element)) return;
    const band = window.innerHeight * 2;
    for (const img of grid.querySelectorAll('img[loading="lazy"]')) {
      if (!(img instanceof HTMLImageElement)) continue;
      const r = img.getBoundingClientRect();
      if (r.bottom > -band && r.top < window.innerHeight + band) {
        img.loading = 'eager';
      }
    }
  }

  /**
   * Mirrors `src/viewport-deferred-images.js` hydrate for listing imgs near the viewport (IO can
   * miss while the grid was masked, or before scroll is applied).
   */
  #hydrateViewportDeferredImagesInBand() {
    const { grid } = this.refs;
    if (!(grid instanceof Element)) return;
    const band = window.innerHeight * 2;
    for (const img of grid.querySelectorAll('img[data-deferred-viewport]')) {
      if (!(img instanceof HTMLImageElement)) continue;
      const r = img.getBoundingClientRect();
      if (r.bottom > -band && r.top < window.innerHeight + band) {
        this.#hydrateSingleViewportDeferredImage(img);
      }
    }
  }

  /** @param {HTMLImageElement} img */
  #hydrateSingleViewportDeferredImage(img) {
    if (img.getAttribute('data-viewport-img-hydrated') === '1') return;
    const srcset = img.getAttribute('data-viewport-srcset');
    const src = img.getAttribute('data-viewport-src');
    if (srcset) {
      img.srcset = srcset;
      img.removeAttribute('data-viewport-srcset');
    }
    if (src) {
      img.src = src;
      img.removeAttribute('data-viewport-src');
    }
    img.removeAttribute('data-deferred-viewport');
    img.removeAttribute('data-viewport-io-bound');
    img.setAttribute('data-viewport-img-hydrated', '1');
  }

  /**
   * When bfcache misses, back/forward can restore a full document whose URL still has `?page=N`
   * while Liquid only SSR’d slice N (first card’s `data-page` is N). A cumulative PLP always starts
   * with page 1 at the top — if not, fetch page 1 HTML and replace the grid before appending 2…N.
   */
  #shouldRebaseGridForBackNavigationCumulative() {
    if (!this.querySelector('[data-collection-load-more]')) return false;
    if (!this.#isBackForwardCollectionNavigation()) return false;
    if (this.#readStoredCollectionLastPage() < 2) return false;

    const u = new URL(window.location.href);
    const raw = u.searchParams.get('page');
    if (raw == null || raw === '') return false;
    const urlPage = Number(raw);
    if (!Number.isFinite(urlPage) || urlPage < 2) return false;

    const { grid } = this.refs;
    if (!grid) return false;
    const first = grid.querySelector(':scope > [ref="cards[]"]');
    if (!first) return false;
    const firstPage = Number(first.dataset.page) || 1;
    return firstPage > 1;
  }

  async #rebaseGridToPage1ForCumulativeRestore() {
    const { grid } = this.refs;
    if (!grid) return;

    for (const key of [...this.pages.keys()]) {
      if (key !== 1) this.pages.delete(key);
    }

    const u1 = new URL(window.location.href);
    u1.searchParams.delete('page');
    u1.hash = '';
    await this.#fetchSpecificPage(1, u1);

    const items1 = this.#getGridForPage(1);
    if (!items1?.length) return;

    grid.replaceChildren(...items1);
    this.#aspectRatioHelper?.processNewElements();
  }

  async #restoreCollectionPagesFromSession() {
    if (!this.querySelector('[data-collection-load-more]')) return;

    const targetLast = this.#readStoredCollectionLastPage();
    if (targetLast < 2) return;

    const { grid } = this.refs;
    if (!grid) return;

    if (this.#shouldRebaseGridForBackNavigationCumulative()) {
      await this.#rebaseGridToPage1ForCumulativeRestore();
    }

    while (this.#maxCardPage() < targetLast) {
      const nextPage = this.#getPage('next');
      if (!nextPage || !this.#shouldUsePage(nextPage)) break;

      let nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolveNextPagePromise = res;
        });
        this.#fetchPage('next');
        await promise;
        nextPageItemElements = this.#getGridForPage(nextPage.page);
        if (!nextPageItemElements) break;
      }

      grid.append(...nextPageItemElements);
      this.#aspectRatioHelper?.processNewElements();
    }

    this.#syncCollectionLoadMore();
    this.#persistCollectionLoadedDepth();
    this.#syncCollectionPageQueryParam();
  }
}
